import { ThemeProvider } from '@emotion/react';
import is from '@sindresorhus/is';
import { type ColumnVisibilityState, flexRender, type OnChangeFn, type SortingState } from '@tanstack/react-table';
import {
    getCoreRowModel,
    getSortedRowModel,
    type LegacyColumnDef,
    type LegacyRow,
    type LegacyTable,
    useLegacyTable,
} from '@tanstack/react-table/legacy';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import { intlFormat, isValid } from 'date-fns';
import debounce from 'lodash/debounce';
import type { ChangeEvent, FocusEvent, FunctionComponent, ReactNode, UIEvent } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { CustomAttribute } from '~/shared/data-contract/custom-attribute';
import type { GeneralFilter } from '~/shared/data-contract/filter';
import { and, contains, equals } from '~/shared/data-contract/filter';
import type {
    FilterState,
    FilterStateDate,
    FilterStateOption,
    FilterWithFilterStates,
} from '~/shared/data-contract/filter-state';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Badge, type BadgeProps } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuSubTriggerContent,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { EmptyStateMessage } from '~/ui/core/empty-state-message/EmptyStateMessage';
import { Filter } from '~/ui/core/filter/Filter';
import { FilterMenu } from '~/ui/core/filter/FilterMenu';
import { GRID_ROW_FOCUS_VISIBLE_CLASS_NAME, useTableRovingFocus } from '~/ui/core/focus/use-table-roving-focus';
import { IconChevronRightSmall, IconPlus, IconSearch, IconSortArrow } from '~/ui/core/icons';
import { Input } from '~/ui/core/input/Input';
import { OverflowMenu } from '~/ui/core/overflow-toolbar/OverflowMenu';
import type { OverflowToolbarItem } from '~/ui/core/overflow-toolbar/OverflowToolbar';
import { OverflowToolbar } from '~/ui/core/overflow-toolbar/OverflowToolbar';
import { PageHeading } from '~/ui/core/page-heading/PageHeading';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Skeleton } from '~/ui/core/skeleton/Skeleton';
import { Spinner } from '~/ui/core/spinner/Spinner';
import {
    TableBody,
    TableCell,
    type TableColumnWidth,
    TableContainer,
    TableErrorMessage,
    TableGrid,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TableSkeletonRow,
} from '~/ui/core/table/Table';
import { CONFIGURE_COLUMNS_TRACK_WIDTH, TableConfigureColumnsCell } from '~/ui/core/table/TableConfigureColumns';
import { useTableColumnWidths } from '~/ui/core/table/use-table-column-widths';
import { BoardContentWithHeader } from '~/ui/domain/board/BoardContentWithHeader';
import { SearchInput } from '~/ui/domain/search-input/SearchInput';
import { theme } from '~/ui/theme/cobrainer-theme';

import { convertFilterStateToFilters } from '../app/common/filter-sidebar/utils';
import { Error403 } from '../app/error-handling/ErrorPages';
import { useSnackbars } from '../app/error-handling/SnackBars';
import { useManySkillsQuery, useSearchSkillsQuery } from '../app/query-hooks/skills';
import { useColumnVisibilitySetting } from '../app/query-hooks/use-column-visibility-setting';
import { useOpenTileSelection, useUrlTiles } from '../url-tiles';
import {
    useInvalidateLearningOpportunityRelatedCaches,
    useIsLearningOpportunityFilterFetching,
    useLearningOpportunitiesPermissions,
    useLearningOpportunityCustomAttributes,
    useLearningOpportunityFilter,
    useLearningOpportunityFilterQuery,
    useLearningOpportunityStateFilter,
    useUpdateLearningOpportunityMutation,
} from './api';
import { COLUMN_LABEL_KEYS, columnAriaSort, customAttributeLabelKey, getColumnWidth, TITLE_COLUMN_ID } from './columns';
import { DateRangeInputs } from './components/DateRangeInputs';
import { DateText } from './components/DateText';
import type { LearningOpportunity, LearningOpportunityState } from './data-contract';
import type { DateFilterRange } from './date-filter';
import { formatDateFilterInput } from './date-filter';
import {
    buildLearningOpportunityStateUpdate,
    type EditableLearningOpportunityState,
    editableLearningOpportunityStates,
    stateTranslationKey,
} from './helpers';
import { useHideableColumns } from './hooks/use-hideable-columns';
import { useRegisterLearningOpportunitiesChatContext } from './hooks/use-learning-opportunity-chat-context';
import { LearningOpportunitiesStatistics } from './LearningOpportunitiesStatistics';
import {
    createLearningOpportunityCreateTilePayload,
    createLearningOpportunityDetailTilePayload,
    LEARNING_OPPORTUNITY_TILE_SLOT_KEY,
    selectOpportunityIdFromTile,
} from './url-tile';

const TABLE_PAGE_SIZE = 50;
const ROW_HEIGHT = 54;
const SKELETON_ROW_COUNT = 5;
const SEARCH_DEBOUNCE_MS = 300;
const learningOpportunityStates: readonly LearningOpportunityState[] = ['all', 'draft', 'published', 'archived'];

const combineFilters = (...filters: readonly (GeneralFilter | undefined)[]): GeneralFilter | undefined => {
    const definedFilters = filters.filter((filter): filter is GeneralFilter => filter !== undefined);

    if (definedFilters.length === 0) return undefined;
    if (definedFilters.length === 1) return definedFilters[0];

    return and<Record<string, Date | boolean | number | string | readonly string[]>>(...definedFilters);
};

const getStateFilter = (state: LearningOpportunityState): GeneralFilter | undefined =>
    state === 'all' ? undefined : (equals('state', state) as GeneralFilter);

const getSearchFilter = (searchTerm: string): GeneralFilter | undefined =>
    is.nonEmptyString(searchTerm) ? (contains('title', searchTerm, true) as GeneralFilter) : undefined;

type SkillLabelMap = Readonly<Record<string, string>>;

const useDebouncedSearch = (): {
    readonly inputValue: string;
    readonly searchTerm: string;
    readonly setInputValue: (event: ChangeEvent<HTMLInputElement>) => void;
} => {
    const [inputValue, updateInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const debounced = useMemo(() => debounce((value: string) => setSearchTerm(value.trim()), SEARCH_DEBOUNCE_MS), []);

    useEffect(() => () => debounced.cancel(), [debounced]);

    const setInputValue = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        updateInputValue(value);
        debounced(value);
    };

    return { inputValue, searchTerm, setInputValue };
};

const useLearningOpportunitiesInfiniteTable = ({
    baseFilter,
    searchTerm,
    sort,
    state,
}: {
    readonly baseFilter?: GeneralFilter;
    readonly searchTerm: string;
    readonly sort: SortingState;
    readonly state: LearningOpportunityState;
}): {
    readonly error: Error | null;
    readonly fetchNextPage: () => void;
    readonly hasNextPage: boolean;
    readonly isFetching: boolean;
    readonly isLoading: boolean;
    readonly opportunities: readonly LearningOpportunity[];
    readonly totalCount: number;
} => {
    const [page, setPage] = useState(0);
    const [opportunities, setOpportunities] = useState<readonly LearningOpportunity[]>([]);
    const filter = useMemo(
        () => combineFilters(baseFilter, getSearchFilter(searchTerm), getStateFilter(state)),
        [baseFilter, searchTerm, state],
    );
    const sorted = useMemo(
        () => ({
            fields: sort.map((item) => ({
                direction: item.desc ? ('desc' as const) : ('asc' as const),
                field: item.id,
            })),
        }),
        [sort],
    );
    const resetKey = useMemo(() => JSON.stringify({ filter, sorted }), [filter, sorted]);
    const query = useLearningOpportunityFilterQuery({
        filter,
        paged: { page, size: TABLE_PAGE_SIZE },
        sorted,
    });
    const totalCount = query.isPlaceholderData ? opportunities.length : (query.data?.pagination.count ?? 0);
    const hasNextPage = opportunities.length < totalCount;

    useEffect(() => {
        void resetKey;
        setPage(0);
        setOpportunities([]);
    }, [resetKey]);

    useEffect(() => {
        if (query.isPlaceholderData) return;

        const pageData = query.data?.data;
        if (pageData === undefined) return;

        setOpportunities((current) => {
            if (page === 0) return pageData;

            const existingIds = new Set(current.map((item) => item.id));
            const nextItems = pageData.filter((item) => !existingIds.has(item.id));

            return [...current, ...nextItems];
        });
    }, [page, query.data, query.isPlaceholderData]);

    const fetchNextPage = (): void => {
        if (query.isFetching || hasNextPage === false) return;
        setPage((current) => current + 1);
    };

    return {
        error: query.error,
        fetchNextPage,
        hasNextPage,
        isFetching: query.isFetching,
        isLoading: query.isPending && page === 0,
        opportunities,
        totalCount,
    };
};

const SortButton: FunctionComponent<{
    readonly children: ReactNode;
    readonly columnId: string;
    readonly table: LegacyTable<LearningOpportunityRow>;
}> = ({ children, columnId, table }) => {
    const active = (table.options.state?.sorting ?? []).find((item) => item.id === columnId);

    return (
        <button
            className="flex appearance-none items-center gap-xs border-0 bg-transparent p-0 text-left font-inherit text-text-muted"
            type="button"
            onClick={() => table.setSorting([{ id: columnId, desc: active?.desc === false }])}
        >
            {children}
            {active !== undefined && (
                <IconSortArrow size={14} style={{ transform: active.desc ? 'rotate(180deg)' : undefined }} />
            )}
        </button>
    );
};

const TranslatedHeader: FunctionComponent<{
    readonly columnId: string;
    readonly fallback?: string;
    readonly label: TranslationKey;
    readonly table: LegacyTable<LearningOpportunityRow>;
}> = ({ columnId, fallback, label, table }) => {
    const translate = useTranslate();

    return (
        <SortButton columnId={columnId} table={table}>
            {translate(label, { defaultValue: fallback })}
        </SortButton>
    );
};

const StaticTranslatedHeader: FunctionComponent<{ readonly label: TranslationKey }> = ({ label }) => {
    const translate = useTranslate();

    return <span>{translate(label)}</span>;
};

type LearningOpportunityRow = LearningOpportunity;

const stateBadgeVariant = (state: LearningOpportunityState): NonNullable<BadgeProps['variant']> =>
    state === 'published' ? 'success' : state === 'archived' ? 'error' : 'gray';

const StateBadgeDropdown: FunctionComponent<{ readonly opportunity: LearningOpportunity }> = ({ opportunity }) => {
    const translate = useTranslate();
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();
    const updateMutation = useUpdateLearningOpportunityMutation('patchJson', opportunity.id);
    const invalidateRelatedCaches = useInvalidateLearningOpportunityRelatedCaches();

    const handleSelect = (next: EditableLearningOpportunityState): void => {
        if (next === opportunity.state || updateMutation.isPending) return;
        updateMutation.mutate(buildLearningOpportunityStateUpdate(next), {
            onError: showErrorSnackbar,
            onSuccess: () => {
                showSuccessSnackbar('success.learning-opportunities.edited');
                invalidateRelatedCaches();
            },
        });
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <button
                    aria-label={translate('pages.learning-opportunities.headlines.state')}
                    className="appearance-none border-0 bg-transparent p-0"
                    disabled={updateMutation.isPending}
                    type="button"
                    onClick={(event) => event.stopPropagation()}
                >
                    <Badge variant={stateBadgeVariant(opportunity.state)}>
                        {translate(stateTranslationKey(opportunity.state))}
                    </Badge>
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
                {editableLearningOpportunityStates.map((stateOption) => (
                    <DropdownMenuItem
                        key={stateOption}
                        disabled={stateOption === opportunity.state}
                        onSelect={() => handleSelect(stateOption)}
                    >
                        {translate(stateTranslationKey(stateOption))}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const CustomAttributeCell: FunctionComponent<{
    readonly attribute: CustomAttribute;
    readonly value: boolean | number | string | null | undefined;
}> = ({ attribute, value }) => {
    const translate = useTranslate();
    const {
        applicationState: { language },
    } = useApplicationContext();

    if (is.nullOrUndefined(value)) return <span>–</span>;

    if (attribute.type === 'boolean') return <span>{translate(value === true ? 'common.yes' : 'common.no')}</span>;

    if (attribute.type === 'date' && is.string(value) && isValid(new Date(value))) {
        return <span>{intlFormat(new Date(value), { locale: language.code })}</span>;
    }

    if (attribute.type === 'string' && is.string(value)) {
        return (
            <span className="truncate">
                {translate(`attributes.learningOpportunity.${attribute.name}-options.${value}`, {
                    defaultValue: value,
                })}
            </span>
        );
    }

    return <span className="truncate">{String(value)}</span>;
};

const createLearningOpportunityColumns = ({
    customAttributes,
    translateProvider,
}: {
    readonly customAttributes: readonly CustomAttribute[];
    readonly translateProvider: (provider: string | undefined) => string;
}): readonly LegacyColumnDef<LearningOpportunityRow>[] => [
    {
        accessorKey: 'title',
        cell: ({ row }) => (
            <span className="min-w-0 truncate text-text-primary in-data-[is-active=true]:!text-text-on-accent">
                {row.original.title}
            </span>
        ),
        enableHiding: false,
        header: ({ table }) => <TranslatedHeader columnId="title" label={COLUMN_LABEL_KEYS.title} table={table} />,
    },
    {
        accessorKey: 'externalId',
        cell: ({ row }) => <span className="truncate">{row.original.externalId ?? '–'}</span>,
        enableSorting: false,
        header: () => <StaticTranslatedHeader label={COLUMN_LABEL_KEYS.externalId} />,
    },
    {
        accessorKey: 'updatedAt',
        cell: ({ row }) => <DateText value={row.original.updatedAt} />,
        header: ({ table }) => (
            <TranslatedHeader columnId="updatedAt" label={COLUMN_LABEL_KEYS.updatedAt} table={table} />
        ),
    },
    {
        accessorKey: 'provider',
        cell: ({ row }) => <span className="truncate">{translateProvider(row.original.provider)}</span>,
        header: ({ table }) => (
            <TranslatedHeader columnId="provider" label={COLUMN_LABEL_KEYS.provider} table={table} />
        ),
    },
    {
        accessorKey: 'state',
        cell: ({ row }) => <StateBadgeDropdown opportunity={row.original} />,
        header: ({ table }) => <TranslatedHeader columnId="state" label={COLUMN_LABEL_KEYS.state} table={table} />,
    },
    {
        accessorKey: 'ownerName',
        cell: ({ row }) => <span className="truncate">{row.original.ownerName ?? '–'}</span>,
        header: ({ table }) => (
            <TranslatedHeader columnId="ownerName" label={COLUMN_LABEL_KEYS.ownerName} table={table} />
        ),
    },
    {
        accessorKey: 'createdAt',
        cell: ({ row }) => <DateText value={row.original.createdAt} />,
        header: ({ table }) => (
            <TranslatedHeader columnId="createdAt" label={COLUMN_LABEL_KEYS.createdAt} table={table} />
        ),
    },
    ...customAttributes.map(
        (attribute): LegacyColumnDef<LearningOpportunityRow> => ({
            accessorFn: (row) => row.custom?.[attribute.name],
            cell: ({ row }) => (
                <CustomAttributeCell attribute={attribute} value={row.original.custom?.[attribute.name]} />
            ),
            header: ({ table }) => (
                <TranslatedHeader
                    columnId={attribute.key}
                    fallback={attribute.name}
                    label={customAttributeLabelKey(attribute.name)}
                    table={table}
                />
            ),
            id: attribute.key,
        }),
    ),
];

const FORCED_VISIBLE_COLUMN_IDS: readonly string[] = [TITLE_COLUMN_ID];

const VirtualLearningOpportunityRow: FunctionComponent<{
    readonly columnWidths: readonly TableColumnWidth[];
    readonly isSelected: boolean;
    readonly onSelect: (id: string) => void;
    readonly row: LegacyRow<LearningOpportunityRow>;
    readonly rowProps: {
        readonly tabIndex: number;
        readonly onFocus: (event: FocusEvent<HTMLElement>) => void;
    };
    readonly virtualRow: VirtualItem;
}> = memo(({ columnWidths, isSelected, onSelect, row, rowProps, virtualRow }) => {
    const handleSelect = (): void => onSelect(row.original.id);

    return (
        <div
            className="absolute left-0 top-0 w-full bg-surface-canvas py-[3px]"
            data-index={virtualRow.index}
            style={{ transform: `translateY(${virtualRow.start}px)` }}
        >
            <TableRow
                {...rowProps}
                $columnWidths={columnWidths}
                $isSelectable={true}
                $isSelected={isSelected}
                className={`cursor-pointer ${GRID_ROW_FOCUS_VISIBLE_CLASS_NAME}`}
                role="row"
                style={{ marginTop: 0 }}
                onClick={handleSelect}
            >
                {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} data-column={cell.column.id} role="gridcell">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                ))}
            </TableRow>
        </div>
    );
});

const LearningOpportunitiesTable: FunctionComponent<{
    readonly columnVisibility: ColumnVisibilityState;
    readonly customAttributes: readonly CustomAttribute[];
    readonly filter?: GeneralFilter;
    readonly onColumnVisibilityChange: OnChangeFn<ColumnVisibilityState>;
    readonly searchTerm: string;
    readonly state: LearningOpportunityState;
}> = ({ columnVisibility, customAttributes, filter, onColumnVisibilityChange, searchTerm, state }) => {
    const translate = useTranslate();
    const { openTile } = useUrlTiles();
    const [sorting, setSorting] = useState<SortingState>([{ id: 'updatedAt', desc: true }]);
    const visibleSorting = useMemo(() => {
        const filtered = sorting.filter((item) => columnVisibility[item.id] !== false);

        // Fall back to the always-visible title column so hiding the sorted column never
        // leaves the list in an undefined, backend-decided order.
        return filtered.length > 0 ? filtered : [{ desc: false, id: TITLE_COLUMN_ID }];
    }, [columnVisibility, sorting]);
    const { error, fetchNextPage, hasNextPage, isFetching, isLoading, opportunities } =
        useLearningOpportunitiesInfiniteTable({
            baseFilter: filter,
            searchTerm,
            sort: visibleSorting,
            state,
        });
    const {
        applicationState: { language },
        translate: translateFromContext,
    } = useApplicationContext();
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const columns = useMemo(
        () =>
            createLearningOpportunityColumns({
                customAttributes,
                translateProvider: (provider) =>
                    is.nonEmptyString(provider) ? translateFromContext(`providers.${provider}` as TranslationKey) : '–',
            }),
        [customAttributes, translateFromContext],
    );
    const handleRowSelect = useCallback(
        (id: string): void => {
            openTile(createLearningOpportunityDetailTilePayload(id));
        },
        [openTile],
    );
    const isSelected = useOpenTileSelection({
        selectId: selectOpportunityIdFromTile,
        slotKey: LEARNING_OPPORTUNITY_TILE_SLOT_KEY,
    });
    const table = useLegacyTable({
        columns: columns as LegacyColumnDef<LearningOpportunityRow>[],
        data: opportunities as LearningOpportunityRow[],
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.id,
        getSortedRowModel: getSortedRowModel(),
        manualSorting: true,
        onColumnVisibilityChange,
        onSortingChange: setSorting,
        state: { columnVisibility, sorting: visibleSorting },
    });
    const rows = table.getRowModel().rows;
    const { gridColumnWidths, visibleColumnWidths } = useTableColumnWidths({
        columnIds: table.getVisibleLeafColumns().map((column) => column.id),
        getColumnWidth,
        trailingTrackWidth: CONFIGURE_COLUMNS_TRACK_WIDTH,
    });
    const hideableColumns = useHideableColumns(customAttributes);
    const isFetchingMore = isFetching && isLoading === false;
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        estimateSize: () => ROW_HEIGHT,
        getScrollElement: () => scrollContainerRef.current,
        overscan: 5,
    });

    const {
        getRegionProps,
        getRowProps,
        onKeyDown: handleGridKeyDown,
    } = useTableRovingFocus({
        onOpen: (row) => handleRowSelect(row.original.id),
        regionKey: 'learning-opportunities-table',
        rows,
        scrollContainerRef,
        scrollToIndex: (index) => rowVirtualizer.scrollToIndex(index),
    });

    const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
        const target = event.currentTarget;
        const isNearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 160;

        if (isNearBottom && hasNextPage && isFetching === false) fetchNextPage();
    };

    if (error !== null) {
        return (
            <TableContainer>
                <TableErrorMessage>{error.message}</TableErrorMessage>
            </TableContainer>
        );
    }

    return (
        <TableContainer className="mr-m">
            <TableGrid
                aria-label="Learning opportunities table"
                className="grid-rows-[auto_1fr] h-full"
                role="grid"
                onKeyDown={handleGridKeyDown}
            >
                <TableHeader $columnWidths={gridColumnWidths} role="row">
                    {table.getHeaderGroups().map((headerGroup) =>
                        headerGroup.headers.map((header) => (
                            <TableHeaderCell
                                key={header.id}
                                aria-sort={
                                    header.column.getCanSort()
                                        ? columnAriaSort(visibleSorting, header.column.id)
                                        : undefined
                                }
                                data-column={header.column.id}
                                role="columnheader"
                            >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                            </TableHeaderCell>
                        )),
                    )}
                    <TableConfigureColumnsCell
                        columns={hideableColumns}
                        columnVisibility={columnVisibility}
                        triggerAriaLabel={translate('components.table.configure-columns')}
                        onColumnVisibilityChange={onColumnVisibilityChange}
                    />
                </TableHeader>
                <TableBody
                    ref={scrollContainerRef}
                    data-testid="learning-opportunities-table-body"
                    {...getRegionProps()}
                    onScroll={handleScroll}
                >
                    {isLoading ? (
                        Array.from({ length: 20 }).map((_, index) => (
                            <TableSkeletonRow
                                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows are static placeholders.
                                key={index}
                                columnCount={visibleColumnWidths.length}
                                columnWidths={gridColumnWidths}
                            />
                        ))
                    ) : rows.length === 0 ? (
                        <div className="flex h-full p-s">
                            <EmptyStateMessage placement="one-third">
                                {translate('pages.global-search.no-data.learning-opportunities' as TranslationKey)}
                            </EmptyStateMessage>
                        </div>
                    ) : (
                        <div
                            style={{
                                height: `${
                                    rowVirtualizer.getTotalSize() +
                                    (isFetchingMore ? SKELETON_ROW_COUNT * ROW_HEIGHT : 0)
                                }px`,
                                position: 'relative',
                                width: '100%',
                            }}
                        >
                            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                const row = rows[virtualRow.index];

                                return row === undefined ? null : (
                                    <VirtualLearningOpportunityRow
                                        key={`${row.id}-${language.code}`}
                                        columnWidths={gridColumnWidths}
                                        isSelected={isSelected(row.original.id)}
                                        row={row}
                                        rowProps={getRowProps(virtualRow.index)}
                                        virtualRow={virtualRow}
                                        onSelect={handleRowSelect}
                                    />
                                );
                            })}
                            {isFetchingMore &&
                                Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => {
                                    const offset = rowVirtualizer.getTotalSize() + index * ROW_HEIGHT;

                                    return (
                                        <div
                                            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows are static placeholders.
                                            key={`skeleton-${index}`}
                                            className="absolute left-0 top-0 w-full"
                                            style={{ transform: `translateY(${offset}px)` }}
                                        >
                                            <TableSkeletonRow
                                                columnCount={visibleColumnWidths.length}
                                                columnWidths={gridColumnWidths}
                                            />
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </TableBody>
            </TableGrid>
        </TableContainer>
    );
};

const isStringArray = (value: unknown): value is readonly string[] => is.array(value, is.string);

const emptySelectedFilterValues: readonly string[] = [];

const getSelectedValues = (filterState: FilterState): readonly string[] =>
    isStringArray(filterState.value) ? filterState.value : emptySelectedFilterValues;

const getSelectedSkillIds = (filters: readonly FilterWithFilterStates[]): readonly string[] =>
    filters.flatMap((filterGroup) =>
        filterGroup.filterStates.flatMap((filterState) =>
            filterState.type === 'skill' ? getSelectedValues(filterState) : [],
        ),
    );

const hasDateValue = (filterState: FilterStateDate): boolean =>
    is.array(filterState.value) && filterState.value.some((value) => is.date(value));

const getFilterStateValueCount = (filterState: FilterState): number => {
    if (
        (filterState.type === 'string' ||
            filterState.type === 'integer' ||
            filterState.type === 'user' ||
            filterState.type === 'skill' ||
            filterState.type === 'uuid') &&
        isStringArray(filterState.value)
    ) {
        return filterState.value.length;
    }

    if (filterState.type === 'boolean') return is.boolean(filterState.value) ? 1 : 0;
    if (filterState.type === 'date') return hasDateValue(filterState) ? 1 : 0;
    if (filterState.type === 'percent') return is.array(filterState.value) ? 1 : 0;

    return 0;
};

const updateFilterStateValue = (
    filters: readonly FilterWithFilterStates[],
    field: string,
    value: FilterState['value'],
): readonly FilterWithFilterStates[] =>
    filters.map((filterGroup) => ({
        ...filterGroup,
        filterStates: filterGroup.filterStates.map((filterState) =>
            filterState.field === field ? ({ ...filterState, value } as FilterState) : filterState,
        ),
    })) as readonly FilterWithFilterStates[];

const toggleFilterStateValue = (
    filters: readonly FilterWithFilterStates[],
    filterState: FilterState,
    value: string,
): readonly FilterWithFilterStates[] => {
    const selectedValues = getSelectedValues(filterState);
    const nextValues = selectedValues.includes(value)
        ? selectedValues.filter((selectedValue) => selectedValue !== value)
        : [...selectedValues, value];

    return updateFilterStateValue(filters, filterState.field, nextValues.length > 0 ? nextValues : undefined);
};

const optionLabel = (options: readonly FilterStateOption[] | undefined, value: string): string =>
    options?.find((option) => option.id === value)?.label ?? value;

const OptionFilterSubmenu: FunctionComponent<{
    readonly filterState: FilterState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
}> = ({ filterState, filters, onFiltersChange }) => {
    if (!('options' in filterState) || is.emptyArray(filterState.options)) return null;

    const selectedValues = getSelectedValues(filterState);

    return (
        <div className="flex max-h-[32vh] min-w-[240px] flex-col gap-[2px]">
            {filterState.options.map((option) => (
                <DropdownMenuCheckboxItem
                    key={option.id}
                    checked={selectedValues.includes(option.id)}
                    onCheckedChange={() => onFiltersChange(toggleFilterStateValue(filters, filterState, option.id))}
                >
                    <span className="min-w-0 flex-1 truncate text-text-form">{option.label}</span>
                </DropdownMenuCheckboxItem>
            ))}
        </div>
    );
};

const SkillFilterSubmenu: FunctionComponent<{
    readonly filterState: FilterState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
    readonly onSkillLabelsChange: (skillLabels: SkillLabelMap) => void;
}> = ({ filterState, filters, onFiltersChange, onSkillLabelsChange }) => {
    const translate = useTranslate();
    const [searchValue, setSearchValue] = useState('');
    const { data: searchResults, isFetching } = useSearchSkillsQuery(searchValue, ['hard', 'soft', 'language'], 50, {
        allowEmptySearch: false,
    });
    const selectedValues = getSelectedValues(filterState);
    const { data: selectedSkills } = useManySkillsQuery({
        enabled: is.nonEmptyArray(selectedValues),
        skillIds: selectedValues,
    });
    const selectedIds = useMemo(() => new Set(selectedValues), [selectedValues]);
    const visibleSkills = useMemo(() => {
        const fetchedSkills = searchResults ?? [];
        const selectedSkillsById = new Map((selectedSkills ?? []).map((skill) => [skill.id, skill]));
        const fetchedIds = new Set(fetchedSkills.map((skill) => skill.id));
        const selectedFallbacks = selectedValues
            .filter((skillId) => fetchedIds.has(skillId) === false)
            .map((skillId) => selectedSkillsById.get(skillId) ?? { id: skillId, title: skillId });

        return [...selectedFallbacks, ...fetchedSkills];
    }, [searchResults, selectedSkills, selectedValues]);

    useEffect(() => {
        if (visibleSkills.length === 0) return;

        onSkillLabelsChange(Object.fromEntries(visibleSkills.map((skill) => [skill.id, skill.title])) as SkillLabelMap);
    }, [onSkillLabelsChange, visibleSkills]);

    return (
        <div className="flex min-w-[320px] flex-col gap-s">
            <div className="relative flex items-center gap-s border-b border-border-muted px-s pb-s">
                <span className="flex items-center text-text-content-subtle opacity-50">
                    <IconSearch size={16} />
                </span>
                <Input
                    autoFocus={true}
                    className="pl-s font-normal text-text-content-subtle text-body-xxs leading-[1.25rem]"
                    placeholder={translate('pages.job-architecture-v2.job-profile-skill-search-placeholder')}
                    value={searchValue}
                    variant="ghost"
                    onChange={(event) => setSearchValue(event.target.value)}
                    onKeyDown={(event) => event.stopPropagation()}
                />
                {isFetching && <Spinner style={{ position: 'absolute', right: '0.5rem' }} />}
            </div>
            <ScrollArea orientation="vertical">
                <div className="flex max-h-[32vh] flex-col gap-[2px]">
                    {visibleSkills.length === 0 && isFetching === false && (
                        <div className="px-m py-m text-center text-text-disabled leading-[1.25rem]">
                            {translate('pages.global-search.no-data.skills')}
                        </div>
                    )}
                    {visibleSkills.map((skill) => (
                        <DropdownMenuCheckboxItem
                            key={skill.id}
                            checked={selectedIds.has(skill.id)}
                            onCheckedChange={() =>
                                onFiltersChange(toggleFilterStateValue(filters, filterState, skill.id))
                            }
                        >
                            <span className="min-w-0 flex-1 truncate text-text-form">{skill.title}</span>
                        </DropdownMenuCheckboxItem>
                    ))}
                </div>
            </ScrollArea>
        </div>
    );
};

const BooleanFilterSubmenu: FunctionComponent<{
    readonly filterState: FilterState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
}> = ({ filterState, filters, onFiltersChange }) => {
    const translate = useTranslate();
    if (filterState.type !== 'boolean') return null;

    return (
        <div className="flex min-w-[180px] flex-col gap-[2px]">
            {[
                { label: translate('common.yes'), value: true },
                { label: translate('common.no'), value: false },
            ].map((option) => (
                <DropdownMenuCheckboxItem
                    key={String(option.value)}
                    checked={filterState.value === option.value}
                    onCheckedChange={() =>
                        onFiltersChange(updateFilterStateValue(filters, filterState.field, option.value))
                    }
                >
                    {option.label}
                </DropdownMenuCheckboxItem>
            ))}
            {is.boolean(filterState.value) && (
                <DropdownMenuItem
                    onSelect={(event) => {
                        event.preventDefault();
                        onFiltersChange(updateFilterStateValue(filters, filterState.field, undefined));
                    }}
                >
                    {translate('common.reset')}
                </DropdownMenuItem>
            )}
        </div>
    );
};

const DateFilterSubmenu: FunctionComponent<{
    readonly filterState: FilterState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
}> = ({ filterState, filters, onFiltersChange }) => {
    if (filterState.type !== 'date') return null;

    const [startValue, endValue] = is.array(filterState.value) ? filterState.value : [null, null];
    const range: DateFilterRange = [is.date(startValue) ? startValue : null, is.date(endValue) ? endValue : null];

    return (
        <DateRangeInputs
            field={filterState.field}
            range={range}
            onRangeChange={(nextRange) =>
                onFiltersChange(updateFilterStateValue(filters, filterState.field, nextRange))
            }
        />
    );
};

const PercentFilterSubmenu: FunctionComponent<{
    readonly filterState: FilterState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
}> = ({ filterState, filters, onFiltersChange }) => {
    if (filterState.type !== 'percent') return null;

    const [startValue = 0, endValue = 1] = is.array(filterState.value) ? filterState.value : [0, 1];
    const updatePercent = (index: 0 | 1, value: string): void => {
        const numberValue = Math.max(0, Math.min(100, Number(value)));
        const nextValue: [number, number] = [startValue, endValue];
        nextValue[index] = numberValue / 100;
        onFiltersChange(updateFilterStateValue(filters, filterState.field, nextValue));
    };

    return (
        <div className="flex min-w-[240px] gap-s p-s">
            <Input
                aria-label={`${filterState.label} min`}
                max={100}
                min={0}
                type="number"
                value={Math.round(startValue * 100)}
                onChange={(event) => updatePercent(0, event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
            />
            <Input
                aria-label={`${filterState.label} max`}
                max={100}
                min={0}
                type="number"
                value={Math.round(endValue * 100)}
                onChange={(event) => updatePercent(1, event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
            />
        </div>
    );
};

const DynamicFilterSubmenuContent: FunctionComponent<{
    readonly filterState: FilterState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
    readonly onSkillLabelsChange: (skillLabels: SkillLabelMap) => void;
}> = ({ filterState, filters, onFiltersChange, onSkillLabelsChange }) => {
    if (filterState.type === 'skill') {
        return (
            <SkillFilterSubmenu
                filterState={filterState}
                filters={filters}
                onFiltersChange={onFiltersChange}
                onSkillLabelsChange={onSkillLabelsChange}
            />
        );
    }

    if (filterState.type === 'boolean') {
        return <BooleanFilterSubmenu filterState={filterState} filters={filters} onFiltersChange={onFiltersChange} />;
    }

    if (filterState.type === 'date') {
        return <DateFilterSubmenu filterState={filterState} filters={filters} onFiltersChange={onFiltersChange} />;
    }

    if (filterState.type === 'percent') {
        return <PercentFilterSubmenu filterState={filterState} filters={filters} onFiltersChange={onFiltersChange} />;
    }

    if (filterState.type === 'string' || filterState.type === 'integer' || filterState.type === 'user') {
        return <OptionFilterSubmenu filterState={filterState} filters={filters} onFiltersChange={onFiltersChange} />;
    }

    return null;
};

const getDateFilterActiveLabel = (filterState: FilterStateDate): string | undefined => {
    if (!hasDateValue(filterState)) {
        return undefined;
    }

    const [startValue, endValue] = filterState.value ?? [null, null];
    const startLabel = is.date(startValue) ? formatDateFilterInput(startValue) : '...';
    const endLabel = is.date(endValue) ? formatDateFilterInput(endValue) : '...';

    return `${startLabel} - ${endLabel}`;
};

const getSelectedFilterActiveLabel = ({
    filterState,
    skillLabels,
}: {
    readonly filterState: FilterState;
    readonly skillLabels: SkillLabelMap;
}): string | undefined => {
    const selectedValues = getSelectedValues(filterState);
    const selectedValue = selectedValues[0];

    if (selectedValues.length !== 1 || is.undefined(selectedValue)) {
        return undefined;
    }

    return filterState.type === 'skill'
        ? (skillLabels[selectedValue] ?? selectedValue)
        : optionLabel('options' in filterState ? filterState.options : undefined, selectedValue);
};

const getFilterStateActiveLabel = ({
    filterState,
    skillLabels,
    translate,
}: {
    readonly filterState: FilterState;
    readonly skillLabels: SkillLabelMap;
    readonly translate: (key: TranslationKey, values?: Record<string, unknown>) => string;
}): string | undefined => {
    if (filterState.type === 'boolean' && is.boolean(filterState.value)) {
        return translate(filterState.value ? 'common.yes' : 'common.no');
    }

    if (filterState.type === 'date' && hasDateValue(filterState)) {
        return getDateFilterActiveLabel(filterState);
    }

    if (filterState.type === 'percent' && is.array(filterState.value)) {
        const [startValue, endValue] = filterState.value;

        return `${Math.round(startValue * 100)}% - ${Math.round(endValue * 100)}%`;
    }

    if (
        filterState.type === 'string' ||
        filterState.type === 'integer' ||
        filterState.type === 'user' ||
        filterState.type === 'skill' ||
        filterState.type === 'uuid'
    ) {
        return getSelectedFilterActiveLabel({ filterState, skillLabels });
    }

    return undefined;
};

const MenuSubTrigger: FunctionComponent<{
    readonly children: ReactNode;
    readonly count: number;
    readonly disabled: boolean;
}> = ({ children, count, disabled }) => (
    <DropdownMenuSubTrigger disabled={disabled}>
        <DropdownMenuSubTriggerContent>
            <span>{children}</span>
        </DropdownMenuSubTriggerContent>
        <span className="flex items-center gap-s">
            {count > 0 ? (
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-orange-800 px-xs text-body-xxs font-medium tabular-nums text-white-200">
                    {count}
                </span>
            ) : null}
            <IconChevronRightSmall size={14} />
        </span>
    </DropdownMenuSubTrigger>
);

const useLearningOpportunityFilterControls = ({
    activeState,
    disabled,
    filters,
    onFiltersChange,
    onSkillLabelsChange,
    onStateChange,
    skillLabels,
}: {
    readonly activeState: LearningOpportunityState;
    readonly disabled: boolean;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
    readonly onSkillLabelsChange: (skillLabels: SkillLabelMap) => void;
    readonly onStateChange: (state: LearningOpportunityState) => void;
    readonly skillLabels: SkillLabelMap;
}): readonly { readonly control: ReactNode; readonly id: string; readonly menu: ReactNode }[] => {
    const translate = useTranslate();
    const clearFilterLabel = translate('common.reset');
    const formatSelectedCount = (count: number): string =>
        translate('pages.job-architecture-v2.skills.filter-selected-count', { count });
    const stateOptions = useMemo(
        () =>
            learningOpportunityStates
                .filter((state) => state !== 'all')
                .map((state) => ({
                    label: translate(stateTranslationKey(state)),
                    value: state,
                })),
        [translate],
    );

    const stateLabel = translate('pages.learning-opportunities.headlines.state');

    return [
        {
            control: (
                <Filter<LearningOpportunityState>
                    disabled={disabled}
                    isMultiSelect={false}
                    clearLabel={clearFilterLabel}
                    options={stateOptions}
                    value={activeState === 'all' ? undefined : activeState}
                    onValueChange={(nextState) => onStateChange(nextState ?? 'all')}
                >
                    {stateLabel}
                </Filter>
            ),
            id: 'state',
            menu: (
                <DropdownMenuSub>
                    <MenuSubTrigger count={activeState === 'all' ? 0 : 1} disabled={disabled}>
                        {stateLabel}
                    </MenuSubTrigger>
                    <DropdownMenuSubContent>
                        {learningOpportunityStates.map((state) => (
                            <DropdownMenuCheckboxItem
                                key={state}
                                checked={activeState === state}
                                onCheckedChange={() => onStateChange(state)}
                            >
                                {translate(stateTranslationKey(state))}
                            </DropdownMenuCheckboxItem>
                        ))}
                    </DropdownMenuSubContent>
                </DropdownMenuSub>
            ),
        },
        ...filters.flatMap((filterGroup) =>
            filterGroup.filterStates.map((filterState) => ({
                control: (
                    <FilterMenu
                        activeCount={getFilterStateValueCount(filterState)}
                        disabled={disabled}
                        activeLabel={getFilterStateActiveLabel({ filterState, skillLabels, translate })}
                        clearLabel={clearFilterLabel}
                        content={
                            <DynamicFilterSubmenuContent
                                filterState={filterState}
                                filters={filters}
                                onFiltersChange={onFiltersChange}
                                onSkillLabelsChange={onSkillLabelsChange}
                            />
                        }
                        formatActiveCount={formatSelectedCount}
                        onClear={() => onFiltersChange(updateFilterStateValue(filters, filterState.field, undefined))}
                    >
                        {filterState.label}
                    </FilterMenu>
                ),
                id: `${filterGroup.id}-${filterState.field}`,
                menu: (
                    <DropdownMenuSub>
                        <MenuSubTrigger count={getFilterStateValueCount(filterState)} disabled={disabled}>
                            {filterState.label}
                        </MenuSubTrigger>
                        <DropdownMenuSubContent>
                            <DynamicFilterSubmenuContent
                                filterState={filterState}
                                filters={filters}
                                onFiltersChange={onFiltersChange}
                                onSkillLabelsChange={onSkillLabelsChange}
                            />
                        </DropdownMenuSubContent>
                    </DropdownMenuSub>
                ),
            })),
        ),
    ];
};

const LearningOpportunitiesMainTile: FunctionComponent = () => {
    useRegisterLearningOpportunitiesChatContext();
    const translate = useTranslate();
    const { openTile } = useUrlTiles();
    const location = useLocation();
    const { canUserCreateLearningOpportunities } = useLearningOpportunitiesPermissions();
    const customAttributes = useLearningOpportunityCustomAttributes();
    const customAttributeKeys = useMemo(() => customAttributes.map((attribute) => attribute.key), [customAttributes]);
    const { columnVisibility, onColumnVisibilityChange } = useColumnVisibilitySetting(
        'learningOpportunitiesColumnVisibility',
        { defaultHidden: customAttributeKeys, forcedVisible: FORCED_VISIBLE_COLUMN_IDS },
    );
    const filterQuery = useLearningOpportunityFilter();
    const isTableFetching = useIsLearningOpportunityFilterFetching();
    const { inputValue, searchTerm, setInputValue } = useDebouncedSearch();
    const [state, setState] = useLearningOpportunityStateFilter();
    const [skillLabels, setSkillLabels] = useState<SkillLabelMap>({});
    const [optimisticFilters, setOptimisticFilters] = useState<readonly FilterWithFilterStates[] | undefined>();
    const dynamicFilters = filterQuery.data?.filters ?? [];
    const currentFilters = optimisticFilters ?? dynamicFilters;
    const selectedSkillIds = useMemo(() => getSelectedSkillIds(currentFilters), [currentFilters]);
    const { data: selectedSkills } = useManySkillsQuery({
        enabled: is.nonEmptyArray(selectedSkillIds),
        skillIds: selectedSkillIds,
    });
    const dynamicFilter = useMemo(
        () => convertFilterStateToFilters(currentFilters.flatMap((filterGroup) => filterGroup.filterStates)),
        [currentFilters],
    );
    const listFilter = useMemo(
        () => combineFilters(dynamicFilter, getSearchFilter(searchTerm), getStateFilter(state)),
        [dynamicFilter, searchTerm, state],
    );
    const updateSkillLabels = useCallback((nextSkillLabels: SkillLabelMap): void => {
        setSkillLabels((currentSkillLabels) => {
            const hasChanges = Object.entries(nextSkillLabels).some(
                ([skillId, label]) => currentSkillLabels[skillId] !== label,
            );

            return hasChanges ? { ...currentSkillLabels, ...nextSkillLabels } : currentSkillLabels;
        });
    }, []);
    const updateFilters = useCallback(
        (updatedFilters: readonly FilterWithFilterStates[]): void => {
            setOptimisticFilters(updatedFilters);
            filterQuery.data?.saveFilterValues(updatedFilters);
        },
        [filterQuery.data],
    );
    // biome-ignore lint/correctness/useExhaustiveDependencies: the URL query string is the source of truth for persisted filters.
    useEffect(() => {
        setOptimisticFilters(undefined);
    }, [location.search]);
    useEffect(() => {
        if (is.emptyArray(selectedSkills ?? [])) return;

        updateSkillLabels(Object.fromEntries((selectedSkills ?? []).map((skill) => [skill.id, skill.title])));
    }, [selectedSkills, updateSkillLabels]);

    const filterControls = useLearningOpportunityFilterControls({
        activeState: state,
        disabled: isTableFetching,
        filters: currentFilters,
        skillLabels,
        onFiltersChange: updateFilters,
        onSkillLabelsChange: updateSkillLabels,
        onStateChange: setState,
    });
    const searchPlaceholder = translate('pages.learning-opportunities.toolbar.quick-search-placeholder');
    const search = (
        <SearchInput
            className="min-w-[16.25rem] shrink-0"
            placeholder={searchPlaceholder}
            shape="pill"
            size="sm"
            value={inputValue}
            onChange={setInputValue}
        />
    );
    const searchMenu = (
        // biome-ignore lint/a11y/noStaticElementInteractions: propagation boundary so the "…" menu typeahead does not swallow the search keystrokes.
        <div className="px-xs pt-xs" onKeyDown={(event) => event.stopPropagation()}>
            <SearchInput
                autoFocus={true}
                className="min-w-[16.25rem] shrink-0"
                placeholder={searchPlaceholder}
                size="sm"
                value={inputValue}
                onChange={setInputValue}
            />
        </div>
    );
    const createLabel = translate('pages.learning-opportunities.create-button');
    const createButton = canUserCreateLearningOpportunities ? (
        <Button
            iconPosition="leading"
            size="sm"
            variant="secondary"
            onClick={() => openTile(createLearningOpportunityCreateTilePayload())}
        >
            <IconPlus size={14} />
            {createLabel}
        </Button>
    ) : null;
    const createMenuItem = (
        <DropdownMenuItem onSelect={() => openTile(createLearningOpportunityCreateTilePayload())}>
            <IconPlus size={16} />
            {createLabel}
        </DropdownMenuItem>
    );
    // Inline order (left→right): filters, search, then Create on the far right. Under pressure Create
    // overflows first (priority 0), then filters one at a time (rightmost first); search stays longest.
    const toolbarItems: readonly OverflowToolbarItem[] = [
        ...filterControls.map((filter, index) => ({
            children: filter.control,
            id: filter.id,
            overflow: filter.menu,
            priority: filterControls.length - index,
        })),
        { children: search, id: 'search', overflow: searchMenu, priority: filterControls.length + 1 },
        ...(createButton === null
            ? []
            : [{ children: createButton, id: 'create', overflow: createMenuItem, priority: 0 }]),
    ];

    const pageHeading = (
        <PageHeading
            actions={
                <OverflowToolbar
                    items={toolbarItems}
                    renderTrailing={(overflow) => (
                        <OverflowMenu ariaLabel={translate('common.more')} overflow={overflow} />
                    )}
                />
            }
            title={translate('navigation.learning-management')}
        >
            <LearningOpportunitiesStatistics filter={listFilter} />
        </PageHeading>
    );

    return (
        <BoardContentWithHeader
            content={
                <LearningOpportunitiesTable
                    columnVisibility={columnVisibility}
                    customAttributes={customAttributes}
                    filter={dynamicFilter}
                    searchTerm={searchTerm}
                    state={state}
                    onColumnVisibilityChange={onColumnVisibilityChange}
                />
            }
            headerPaddingRight={false}
            pageHeading={pageHeading}
        />
    );
};

export const LearningManagementPage: FunctionComponent = () => {
    const { canUserSeeLearningOpportunities, isPending } = useLearningOpportunitiesPermissions();

    if (isPending) {
        return (
            <div className="p-xxl">
                <Skeleton height="40px" width="40%" />
            </div>
        );
    }

    if (!canUserSeeLearningOpportunities) {
        return <Error403 />;
    }

    return (
        <ThemeProvider theme={theme}>
            <LearningOpportunitiesMainTile />
        </ThemeProvider>
    );
};
